import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';

import {usePrevious} from 'front-onceforall-core/dist/utils/customHooks';

import subscriptionService from '../../services/subscription/SubscriptionService';
import authenticationService from '../../services/authentication/AuthenticationService';
import {setSubscription} from '../../actions/subscription';
import {captureError} from '../../utils/log';
import {recordGAEvent} from '../../utils/analytics';
import ErrorBoundary from '../../components/ErrorBoundary';

import {hasValidSubscription, isOrganisationDifferent} from './utils';
import Switch from './Switch';

const RECORD_NAV_EVENT = ['/organisation/subscription', '/organisation/collection-mandates', '/organisation/broadcast-mandates'];

function OrganisationGate() {
  const [loaded, setLoaded] = React.useState(false);
  const [organisation, subscription] = useSelector(globalState => {
    const orga = globalState.organisation;
    return [orga, globalState.subscriptions[orga?.organisationId]];
  });
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const prevLocation = usePrevious(location);
  const prevOrganisation = usePrevious(organisation);

  const isSubscriptionValid = hasValidSubscription(subscription);

  async function checkOrganisation() {
    try {
      setLoaded(false);

      await authenticationService.updateTokenRoles();

      if (!organisation?.organisationId) {
        history.replace('/global-dashboard');
        return;
      }

      const subscriptionFound = isSubscriptionValid ? false : await subscriptionService.findSubscriptionByOrganisation(organisation.organisationId);

      if (subscriptionFound) {
        dispatch(setSubscription({
          organisationId: organisation.organisationId,
          subscription: subscriptionFound
        }));
      }

      if (!isSubscriptionValid && !subscriptionFound) {
        history.replace('/register-organisation-plan');
      } else if (!organisation.enabled && !authenticationService.roles().isBackOffice()) {
        history.replace('/dashboard');
      }

      setLoaded(true);
    } catch (error) {
      captureError('authenticationService.updateTokenRoles | subscriptionService.findSubscriptionByOrganisation', error);
      history.replace('/global-dashboard');
    }
  }

  React.useEffect(() => {
    checkOrganisation();
  }, []);

  React.useEffect(() => {
    const organisationIsDifferent = isOrganisationDifferent(organisation, prevOrganisation);
    if (organisationIsDifferent || prevLocation?.pathname !== location.pathname) {
      checkOrganisation();
    }
    if (prevLocation?.pathname !== location.pathname && RECORD_NAV_EVENT.includes(location.pathname)) {
      recordGAEvent('Navigation', location.pathname);
    }
  }, [organisation, location.pathname]);

  if (!loaded) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Switch/>
    </ErrorBoundary>
  );
}

export default OrganisationGate;
